import React, { useRef } from 'react';
import { FileText, Download, Printer, X, CheckCircle, Building2, User, Calendar, Clock, MapPin, DollarSign, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function TrainingContract({ booking, onClose }) {
  const contractRef = useRef(null);

  const handlePrint = () => {
    const printContent = contractRef.current.innerHTML;
    const htmlDoc = `<!DOCTYPE html>
      <html>
      <head>
        <title>Training Contract - ${booking?.job_title}</title>
        <style>
          body { font-family: 'Inter', Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; color: #1e293b; }
          h1 { font-size: 24px; color: #0f172a; margin-bottom: 8px; }
          h2 { font-size: 16px; color: #334155; margin-top: 24px; margin-bottom: 12px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px; }
          p { font-size: 13px; line-height: 1.6; margin-bottom: 8px; }
          .header { text-align: center; border-bottom: 2px solid #2563eb; padding-bottom: 20px; margin-bottom: 30px; }
          .logo { font-size: 28px; font-weight: bold; color: #2563eb; }
          .contract-id { font-size: 12px; color: #64748b; }
          .parties { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
          .party-box { background: #f8fafc; padding: 16px; border-radius: 8px; border: 1px solid #e2e8f0; }
          .party-label { font-size: 11px; color: #64748b; text-transform: uppercase; margin-bottom: 8px; }
          .party-name { font-size: 16px; font-weight: 600; color: #0f172a; }
          .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 16px 0; }
          .detail-item { font-size: 13px; }
          .detail-label { color: #64748b; }
          .detail-value { color: #0f172a; font-weight: 500; }
          .payment-box { background: #ecfdf5; padding: 16px; border-radius: 8px; border: 1px solid #a7f3d0; margin: 20px 0; }
          .terms-list { font-size: 12px; line-height: 1.8; }
          .terms-list li { margin-bottom: 4px; }
          .signature-section { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; margin-top: 60px; }
          .signature-box { border-top: 1px solid #0f172a; padding-top: 8px; }
          .signature-label { font-size: 11px; color: #64748b; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #64748b; text-align: center; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>${printContent}</body>
      </html>`;
    const blob = new Blob([htmlDoc], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');
    printWindow.addEventListener('load', () => {
      printWindow.print();
      URL.revokeObjectURL(url);
    });
  };

  if (!booking) return null;

  const contractId = `TS-${new Date().getFullYear()}-${booking.id?.slice(-8).toUpperCase()}`;
  const today = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="training-contract-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-outfit font-semibold text-lg text-slate-900">Training Contract</h2>
              <p className="text-sm text-slate-500">{contractId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Contract Content */}
        <div className="flex-1 overflow-y-auto p-6" ref={contractRef}>
          {/* Header */}
          <div className="header text-center border-b-2 border-blue-600 pb-6 mb-8">
            <div className="logo text-2xl font-bold text-blue-600 mb-2">Training Solutions</div>
            <h1 className="text-xl font-bold text-slate-900 mb-1">TRAINING SERVICE CONTRACT</h1>
            <p className="contract-id text-sm text-slate-500">Contract ID: {contractId}</p>
            <p className="text-sm text-slate-500">Generated on: {today}</p>
          </div>

          {/* Parties */}
          <div className="parties grid md:grid-cols-2 gap-6 mb-8">
            <div className="party-box bg-blue-50 border border-blue-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Building2 className="w-5 h-5 text-blue-600" />
                <span className="party-label text-xs text-blue-600 uppercase font-medium">Corporate (Client)</span>
              </div>
              <p className="party-name text-lg font-semibold text-slate-900">{booking.corporate_name}</p>
            </div>
            <div className="party-box bg-emerald-50 border border-emerald-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-5 h-5 text-emerald-600" />
                <span className="party-label text-xs text-emerald-600 uppercase font-medium">Trainer (Service Provider)</span>
              </div>
              <p className="party-name text-lg font-semibold text-slate-900">{booking.trainer_name}</p>
            </div>
          </div>

          {/* Training Details */}
          <h2 className="font-outfit text-lg font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">
            1. Training Details
          </h2>
          <div className="details-grid grid md:grid-cols-2 gap-4 mb-6">
            <div className="detail-item flex items-start gap-3">
              <FileText className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="detail-label text-xs text-slate-500">Training Title</p>
                <p className="detail-value font-medium text-slate-900">{booking.job_title}</p>
              </div>
            </div>
            <div className="detail-item flex items-start gap-3">
              <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="detail-label text-xs text-slate-500">Date</p>
                <p className="detail-value font-medium text-slate-900">
                  {new Date(booking.start_datetime).toLocaleDateString('en-IN', { 
                    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' 
                  })}
                </p>
              </div>
            </div>
            <div className="detail-item flex items-start gap-3">
              <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="detail-label text-xs text-slate-500">Time</p>
                <p className="detail-value font-medium text-slate-900">
                  {new Date(booking.start_datetime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} - {' '}
                  {new Date(booking.end_datetime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
            <div className="detail-item flex items-start gap-3">
              <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="detail-label text-xs text-slate-500">Mode</p>
                <p className="detail-value font-medium text-slate-900">As per job requirement</p>
              </div>
            </div>
          </div>

          {/* Payment Terms */}
          <h2 className="font-outfit text-lg font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">
            2. Payment Terms
          </h2>
          <div className="payment-box bg-emerald-50 border border-emerald-200 rounded-xl p-5 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-5 h-5 text-emerald-600" />
              <span className="font-semibold text-emerald-900">Total Training Fee</span>
            </div>
            <p className="text-3xl font-bold text-emerald-700 mb-4">₹{booking.amount?.toLocaleString('en-IN')}</p>
            
            <div className="space-y-2 text-sm text-emerald-800">
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <p><strong>Corporate Payment:</strong> 100% payment to be made to Training Solutions before the scheduled training date.</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <p><strong>Trainer Payment:</strong> Amount will be credited to Trainer within <strong>7 (seven) working days</strong> of receipt of payment from the Corporate, subject to successful completion of training.</p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <p><strong>Platform Fee:</strong> 10% of training fee will be retained by Training Solutions as service charge.</p>
              </div>
            </div>
          </div>

          {/* Terms & Conditions */}
          <h2 className="font-outfit text-lg font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">
            3. Terms & Conditions
          </h2>
          <div className="terms-list text-sm text-slate-600 space-y-3 mb-6">
            <div>
              <strong className="text-slate-900">3.1 Service Delivery:</strong>
              <p>The Trainer agrees to deliver the training session as per the agreed specifications, maintaining professional standards and arriving adequately prepared.</p>
            </div>
            <div>
              <strong className="text-slate-900">3.2 Corporate Responsibilities:</strong>
              <p>The Corporate agrees to provide necessary facilities, equipment, and participant access as required for the training session.</p>
            </div>
            <div>
              <strong className="text-slate-900">3.3 Confidentiality:</strong>
              <p>Both parties agree to maintain strict confidentiality of any proprietary information shared during the engagement. This obligation survives for 2 years after training completion.</p>
            </div>
            <div>
              <strong className="text-slate-900">3.4 Intellectual Property:</strong>
              <p>Training materials provided remain the intellectual property of the Trainer. Corporate receives a non-exclusive license for internal use only.</p>
            </div>
            <div>
              <strong className="text-slate-900">3.5 Feedback:</strong>
              <p>Corporate agrees to provide feedback within 48 hours of training completion. Trainer agrees to submit completion confirmation within 48 hours.</p>
            </div>
          </div>

          {/* Cancellation Policy */}
          <h2 className="font-outfit text-lg font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">
            4. Cancellation Policy
          </h2>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-3 text-left text-slate-700 font-medium">Timeline</th>
                  <th className="px-4 py-3 text-left text-slate-700 font-medium">Corporate Refund</th>
                  <th className="px-4 py-3 text-left text-slate-700 font-medium">Trainer Compensation</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-slate-200">
                  <td className="px-4 py-3 text-slate-600">7+ days before training</td>
                  <td className="px-4 py-3 text-slate-600">100% refund</td>
                  <td className="px-4 py-3 text-slate-600">No compensation</td>
                </tr>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td className="px-4 py-3 text-slate-600">3-7 days before training</td>
                  <td className="px-4 py-3 text-slate-600">50% refund</td>
                  <td className="px-4 py-3 text-slate-600">25% of fee</td>
                </tr>
                <tr className="border-t border-slate-200">
                  <td className="px-4 py-3 text-slate-600">Less than 3 days</td>
                  <td className="px-4 py-3 text-slate-600">No refund</td>
                  <td className="px-4 py-3 text-slate-600">50% of fee</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Dispute Resolution */}
          <h2 className="font-outfit text-lg font-semibold text-slate-900 mb-4 pb-2 border-b border-slate-200">
            5. Dispute Resolution & Governing Law
          </h2>
          <p className="text-sm text-slate-600 mb-6">
            Any disputes arising from this contract shall first be attempted to be resolved through Training Solutions platform mediation. 
            If unresolved, disputes shall be subject to arbitration under the Arbitration and Conciliation Act, with the seat of arbitration 
            in New Delhi, India. This contract is governed by the laws of India.
          </p>

          {/* Signatures */}
          <div className="signature-section grid md:grid-cols-2 gap-12 mt-12 pt-8 border-t border-slate-200">
            <div className="signature-box">
              <p className="text-sm font-medium text-slate-900 mb-8">For Corporate:</p>
              <div className="border-t border-slate-900 pt-2">
                <p className="signature-label text-xs text-slate-500">Authorized Signatory</p>
                <p className="text-sm font-medium text-slate-900">{booking.corporate_name}</p>
              </div>
            </div>
            <div className="signature-box">
              <p className="text-sm font-medium text-slate-900 mb-8">Trainer:</p>
              <div className="border-t border-slate-900 pt-2">
                <p className="signature-label text-xs text-slate-500">Signature</p>
                <p className="text-sm font-medium text-slate-900">{booking.trainer_name}</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="footer mt-12 pt-6 border-t border-slate-200 text-center">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-slate-400" />
              <span className="text-xs text-slate-500">This is a legally binding contract generated by Training Solutions</span>
            </div>
            <p className="text-xs text-slate-400">
              Contract ID: {contractId} | Generated: {today} | www.trainingsolutions.in
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
